import { useEffect, useId, useMemo, useState } from "react";

import { parseReference } from "@medplum/core";
import { DocumentReference, Reference, ServiceRequest } from "@medplum/fhirtypes";
import { TestCoding } from "@medplum/health-gorilla-core";

import { Button, Drawer, Flex, Group, Input, Stack } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { IconCheck, IconDownload, IconX } from "@tabler/icons-react";

import DisabledReason from "@/components/DisabledReason";
import { Loading } from "@/components/Loading";
import { TextWrapper } from "@/components/TextWrapper";
import { DotPhraseRichTextField, PHRASE_TEXT_EXTENSIONS } from "@/dot-phrases";
import { viewFile } from "@/file-uploads/utils/file-uploads";
import { useLabOrderDetails } from "@/health-gorilla/hooks/useLabOrderDetails";
import {
  approveLabOrder,
  updateLabOrderInternalStatus,
  updateLabOrderResultsNote,
} from "@/health-gorilla/services/labs";
import { HealthGorillaLabOrderInternalStatus } from "@/health-gorilla/types/labs";
import {
  toLabResultsNoteHtml,
  toStoredLabResultsNote,
} from "@/health-gorilla/utils/lab-results-note";
import { usePractitionerPermissions } from "@/permission/hooks/usePractitionerPermissions";
import { useAppDispatch } from "@/store";
import { updateLabOrder } from "@/store/slices/lab-orders";
import { showSystemErrorNotification, toast } from "@/utils/notifications";

import LabResultsNoteText from "../LabResultsNoteText";

import LabOrderResultsTable from "./LabOrderResultsTable";
import LabOrderTestDetailsCard from "./LabTestDetailsCard";

interface LabOrderDetailsDrawerProps {
  opened: boolean;
  onClose: () => void;
  labOrder: ServiceRequest | undefined;
}

export default function LabOrderDetailsDrawer({
  opened,
  onClose,
  labOrder,
}: LabOrderDetailsDrawerProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [downloadingFileUrl, setDownloadingFileUrl] = useState<string | undefined>(undefined);
  const dispatch = useAppDispatch();
  const { hasProviderRole } = usePractitionerPermissions();
  const [
    isLoadingRequisitionPDF,
    { open: startLoadingRequisitionPDF, close: stopLoadingRequisitionPDF },
  ] = useDisclosure(false);
  const [requisitionPDFUrl, setRequisitionPDFUrl] = useState<string | undefined>(undefined);
  const resultsNoteLabelId = useId();

  const {
    isLoading,
    medplum,
    performer,
    client,
    clientReference,
    requestor,
    specimenCollectionTime,
    labOrderTestsRequests,
    labOrderTestsResponses,
    diagnoses,
    billTo,
    status,
    orderNotes,
    resultsNote,
    setResultsNote,
    hasResultsNote,
    coverages,
    attachments,
    attachmentsOwnerReference,
    conclusion,
    indication,
  } = useLabOrderDetails(labOrder);

  const showRequisitionPDF = useMemo(() => {
    return (
      HealthGorillaLabOrderInternalStatus.Ordered ===
      (status as HealthGorillaLabOrderInternalStatus)
    );
  }, [status]);

  useEffect(() => {
    if (!showRequisitionPDF) return;
    startLoadingRequisitionPDF();
    (async () => {
      try {
        const reference = labOrder?.supportingInfo?.find(
          (info) =>
            info.display === "LabOrderRequisition" &&
            parseReference(info)[0] === "DocumentReference",
        ) as Reference<DocumentReference> | undefined;

        if (!reference || !reference.reference) return;

        const documentReference = await medplum.readReference<DocumentReference>(reference);

        setRequisitionPDFUrl(documentReference.content?.[0]?.attachment?.url);
      } catch (error) {
        showSystemErrorNotification();
        throw error;
      } finally {
        stopLoadingRequisitionPDF();
      }
    })();
  }, [
    showRequisitionPDF,
    labOrder?.supportingInfo,
    startLoadingRequisitionPDF,
    stopLoadingRequisitionPDF,
    medplum,
  ]);

  const handleOrderStatusChange = async (
    status: HealthGorillaLabOrderInternalStatus,
    title: string,
    message: string,
  ) => {
    if (!labOrder) return;

    const updatedLabOrder = await updateLabOrderInternalStatus(medplum, labOrder, status);
    if (!updatedLabOrder) return;

    toast({
      title,
      message,
      type: "success",
      icon: <IconCheck />,
      autoClose: 5000,
    });

    updateLabOrderStore(updatedLabOrder);
    onClose();
  };

  const updateLabOrderStore = (labOrder: ServiceRequest) => {
    dispatch(
      updateLabOrder({
        id: labOrder.id!,
        changes: { category: labOrder.category, status: labOrder.status, meta: labOrder.meta },
      }),
    );
  };

  const handleApproveOrder = async () => {
    if (!labOrder) return;

    setIsSubmitting(true);
    try {
      const updatedLabOrder = await approveLabOrder(medplum, labOrder);
      if (!updatedLabOrder) return;

      toast({
        title: "Lab order approved",
        message: "This lab order has been approved successfully.",
        type: "success",
        icon: <IconCheck />,
        autoClose: 5000,
      });

      updateLabOrderStore(updatedLabOrder);
      onClose();
    } catch (error) {
      showSystemErrorNotification();
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDenyOrder = async () => {
    setIsSubmitting(true);
    try {
      handleOrderStatusChange(
        HealthGorillaLabOrderInternalStatus.Denied,
        "Lab order denied",
        "This lab order has been denied successfully.",
      );
    } catch (error) {
      showSystemErrorNotification();
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelOrder = async () => {
    setIsSubmitting(true);
    try {
      handleOrderStatusChange(
        HealthGorillaLabOrderInternalStatus.Cancelled,
        "Lab order cancelled",
        "This lab order has been cancelled successfully.",
      );
    } catch (error) {
      showSystemErrorNotification();
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReviewOrder = async () => {
    if (!labOrder || !clientReference?.reference) return;

    setIsSubmitting(true);
    try {
      await updateLabOrderResultsNote(
        medplum,
        labOrder,
        clientReference,
        toStoredLabResultsNote(resultsNote),
      );
      return handleOrderStatusChange(
        HealthGorillaLabOrderInternalStatus.Reviewed,
        "Lab order reviewed",
        "This lab order has been reviewed successfully.",
      );
    } catch (error) {
      showSystemErrorNotification();
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewRequisitionPDF = async () => {
    if (!requisitionPDFUrl) return;
    await viewFile(medplum, `${labOrder?.id}-requisition-form.pdf`, requisitionPDFUrl);
  };

  const canCancelOrder = useMemo(() => {
    return hasProviderRole && status === HealthGorillaLabOrderInternalStatus.Ordered;
  }, [status, hasProviderRole]);

  const canApproveOrDenyOrder = useMemo(() => {
    return hasProviderRole && status === HealthGorillaLabOrderInternalStatus.NeedsApproval;
  }, [status, hasProviderRole]);

  const canReviewOrder = useMemo(() => {
    return hasProviderRole && status === HealthGorillaLabOrderInternalStatus.NeedsReview;
  }, [status, hasProviderRole]);

  const hasResults = useMemo(() => {
    return [
      HealthGorillaLabOrderInternalStatus.NeedsReview,
      HealthGorillaLabOrderInternalStatus.Reviewed,
    ].includes(status as HealthGorillaLabOrderInternalStatus);
  }, [status]);

  const shouldShowResultsNote = hasResults && !canReviewOrder && hasResultsNote;

  const labTests = useMemo(() => {
    return (labOrder?.code?.coding ?? []).filter((coding) => !!coding.code);
  }, [labOrder?.code?.coding]);

  return (
    <Drawer.Root
      trapFocus
      opened={opened}
      position="right"
      onClose={onClose}
      closeOnEscape={false}
      closeOnClickOutside={false}
      size={hasResults ? 896 : 512}
    >
      <Drawer.Overlay />
      <Drawer.Content data-testid="lab-order-details-drawer">
        <Drawer.Header h={64} px="md" bg="gray.0">
          <Flex w="100%" gap="md" align="center" justify="space-between">
            <Flex gap="md" align="center" style={{ flex: 1 }}>
              <Drawer.CloseButton aria-label="Close Lab Order Details Drawer" />
              <Drawer.Title style={{ flex: 1 }}>
                <TextWrapper fz="lg" variant="bold" style={{ wordBreak: "break-word" }}>
                  Lab Order Details
                </TextWrapper>
              </Drawer.Title>
            </Flex>
            {canApproveOrDenyOrder && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isLoading}
                  loading={isSubmitting}
                  onClick={handleDenyOrder}
                  leftSection={<IconX size={16} />}
                >
                  Deny
                </Button>
                <Button
                  type="button"
                  disabled={isLoading}
                  loading={isSubmitting}
                  onClick={handleApproveOrder}
                  leftSection={<IconCheck size={16} />}
                >
                  Approve
                </Button>
              </>
            )}
            {canCancelOrder && (
              <Button
                type="button"
                variant="outline"
                disabled={isLoading}
                loading={isSubmitting}
                onClick={handleCancelOrder}
                leftSection={<IconX size={16} />}
              >
                Cancel Order
              </Button>
            )}
            {canReviewOrder && (
              <Button
                type="button"
                disabled={isLoading}
                loading={isSubmitting}
                onClick={handleReviewOrder}
                data-testid="lab-order-mark-reviewed"
                leftSection={<IconCheck size={16} />}
              >
                Mark Reviewed
              </Button>
            )}
          </Flex>
        </Drawer.Header>
        <Drawer.Body p="lg">
          {isLoading && <Loading height={128} />}
          {!isLoading && labOrder && (
            <Stack gap="lg">
              {performer && (
                <Stack gap="xs">
                  <TextWrapper c="gray.6" variant="bold">
                    Lab
                  </TextWrapper>
                  <TextWrapper aria-label="Lab">{performer}</TextWrapper>
                </Stack>
              )}
              <Stack gap="xs">
                <TextWrapper c="gray.6" variant="bold">
                  Requestor
                </TextWrapper>
                <TextWrapper aria-label="Requestor">{requestor}</TextWrapper>
              </Stack>
              <Stack gap="xs">
                <TextWrapper c="gray.6" variant="bold">
                  Client
                </TextWrapper>
                <TextWrapper aria-label="Client">{client}</TextWrapper>
              </Stack>
              {indication && (
                <Stack gap="xs">
                  <TextWrapper c="gray.6" variant="bold">
                    Indication
                  </TextWrapper>
                  <TextWrapper aria-label="Indication">{indication}</TextWrapper>
                </Stack>
              )}
              {canReviewOrder && (
                <Input.Wrapper
                  labelProps={{ id: resultsNoteLabelId }}
                  label={
                    <TextWrapper variant="bold">
                      Write a custom message to client about lab results
                    </TextWrapper>
                  }
                >
                  <DotPhraseRichTextField
                    showToolbar={false}
                    disabled={isLoading}
                    placeholder="Enter custom message"
                    extensions={PHRASE_TEXT_EXTENSIONS}
                    aria-labelledby={resultsNoteLabelId}
                    data-testid="lab-results-note-input"
                    value={toLabResultsNoteHtml(resultsNote)}
                    onChange={(value) => setResultsNote(value ?? "")}
                  />
                </Input.Wrapper>
              )}
              {shouldShowResultsNote && (
                <Stack gap="xs">
                  <TextWrapper c="gray.6" variant="bold">
                    Custom message to client about lab results:
                  </TextWrapper>
                  <LabResultsNoteText
                    note={resultsNote}
                    aria-label="Custom message to client about lab results"
                  />
                </Stack>
              )}
              {showRequisitionPDF && (
                <Stack gap="xs">
                  <TextWrapper c="gray.6" variant="bold">
                    Requisition Form
                  </TextWrapper>
                  <TextWrapper aria-label="Requisition Form">
                    <DisabledReason
                      reason={
                        requisitionPDFUrl || isLoadingRequisitionPDF
                          ? undefined
                          : "Requisition form not available"
                      }
                    >
                      <Button
                        size="xs"
                        component="a"
                        target="_blank"
                        variant="outline"
                        key={requisitionPDFUrl}
                        disabled={!requisitionPDFUrl}
                        loading={isLoadingRequisitionPDF}
                        onClick={handleViewRequisitionPDF}
                        leftSection={<IconDownload size={16} />}
                      >
                        View Requisition Form
                      </Button>
                    </DisabledReason>
                  </TextWrapper>
                </Stack>
              )}
              {hasResults && attachments.length > 0 && (
                <Stack gap="xs">
                  <TextWrapper c="gray.6" variant="bold">
                    Uploaded File(s)
                  </TextWrapper>
                  <Group>
                    {attachments.map((attachment) => {
                      const fileName =
                        attachment.title ??
                        `${parseReference(clientReference)?.[1] ?? client}-${labOrder.id}`;

                      const handleClick = async () => {
                        try {
                          setDownloadingFileUrl(attachment.url);
                          await viewFile(medplum, fileName, attachment.url, {
                            hash: attachment.hash,
                            ownerReference: attachmentsOwnerReference,
                          });
                        } catch (error) {
                          showSystemErrorNotification();
                          throw error;
                        } finally {
                          setDownloadingFileUrl(undefined);
                        }
                      };
                      return (
                        <Button
                          size="xs"
                          component="a"
                          target="_blank"
                          variant="outline"
                          key={attachment.id}
                          onClick={handleClick}
                          leftSection={<IconDownload size={16} />}
                          loading={downloadingFileUrl === attachment.url}
                          disabled={!!downloadingFileUrl && downloadingFileUrl !== attachment.url}
                        >
                          View Uploaded File
                        </Button>
                      );
                    })}
                  </Group>
                </Stack>
              )}
              {hasResults && labTests.length > 0 && (
                <Stack gap="xs" data-testid="lab-order-details-results">
                  <TextWrapper c="gray.6" variant="bold">
                    Results
                  </TextWrapper>
                  {conclusion && (
                    <TextWrapper aria-label="Results" style={{ whiteSpace: "pre-wrap" }}>
                      {conclusion}
                    </TextWrapper>
                  )}
                  <LabOrderResultsTable labOrder={labOrder} />
                </Stack>
              )}
              {labTests.length > 0 && (
                <Stack gap="sm" aria-label="Tests">
                  <TextWrapper c="gray.6" variant="bold">
                    Tests
                  </TextWrapper>
                  {labTests.map((coding) => (
                    <LabOrderTestDetailsCard
                      key={coding.code}
                      coding={coding as TestCoding}
                      request={labOrderTestsRequests[coding.code!]}
                      response={labOrderTestsResponses[coding.code!]}
                    />
                  ))}
                </Stack>
              )}
              {diagnoses && (
                <Stack gap="xs">
                  <TextWrapper c="gray.6" variant="bold">
                    Diagnoses
                  </TextWrapper>
                  <TextWrapper aria-label="Diagnoses">{diagnoses}</TextWrapper>
                </Stack>
              )}
              {billTo && (
                <Stack gap="xs">
                  <TextWrapper c="gray.6" variant="bold">
                    Bill To
                  </TextWrapper>
                  <TextWrapper aria-label="Bill To">{billTo}</TextWrapper>
                </Stack>
              )}
              {coverages.length > 0 && (
                <Stack gap="xs">
                  <TextWrapper c="gray.6" variant="bold">
                    Insurance
                  </TextWrapper>
                  {coverages.map((coverage) => (
                    <Stack gap={0} key={coverage.id}>
                      <TextWrapper>{coverage.payor?.[0]?.display}</TextWrapper>
                    </Stack>
                  ))}
                </Stack>
              )}
              {orderNotes && (
                <Stack gap="xs">
                  <TextWrapper c="gray.6" variant="bold">
                    Order Notes
                  </TextWrapper>
                  <TextWrapper aria-label="Order Notes">{orderNotes}</TextWrapper>
                </Stack>
              )}
              {specimenCollectionTime && (
                <Stack gap="xs">
                  <TextWrapper c="gray.6" variant="bold">
                    Specimen Collection Time
                  </TextWrapper>
                  <TextWrapper aria-label="Specimen Collection Time">
                    {specimenCollectionTime}
                  </TextWrapper>
                </Stack>
              )}
            </Stack>
          )}
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
}
